// src/pages/configuracion/PerfilPage.tsx
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, Button, Input, Avatar, Badge, Spinner } from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { Camera, Save, Key, User } from 'lucide-react';
import { usePerfil, useActualizarPerfil, useCambiarAvatar } from '@/hooks/queries/useConfiguracion';
import { CambiarPasswordModal } from './components/CambiarPasswordModal';
import { formatDateTime } from '@/lib/utils';

const schema = z.object({
  nombre: z.string().min(1, 'El nombre es requerido'),
  apellido: z.string().optional(),
  telefono: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export function PerfilPage() {
  const { data: perfil, isLoading } = usePerfil();
  const actualizarMutation = useActualizarPerfil();
  const cambiarAvatarMutation = useCambiarAvatar();
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      nombre: '',
      apellido: '',
      telefono: '',
    },
  });

  // Update form when perfil loads
  useEffect(() => {
    if (perfil) {
      form.reset({
        nombre: perfil.nombre ?? '',
        apellido: perfil.apellido ?? '',
        telefono: perfil.telefono ?? '',
      });
    }
  }, [perfil, form]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await cambiarAvatarMutation.mutateAsync(file);
        toast.success('Avatar actualizado correctamente');
      } catch {
        // Error handled by global MutationCache.onError
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      await actualizarMutation.mutateAsync(data);
      toast.success('Perfil actualizado correctamente');
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!perfil) {
    return (
      <div className="text-center py-12">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">No se pudo cargar el perfil</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Mi Perfil</h1>

      {/* Avatar y datos basicos */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="relative">
            <Avatar
              src={perfil.avatar_url}
              fallback={`${perfil.nombre} ${perfil.apellido || ''}`}
              size="xl"
            />
            <label className="absolute bottom-0 right-0 p-1.5 bg-blue-600 text-white rounded-full cursor-pointer hover:bg-blue-700 transition-colors">
              <Camera className="h-4 w-4" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
                disabled={cambiarAvatarMutation.isPending}
              />
            </label>
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900">
              {perfil.nombre} {perfil.apellido}
            </h2>
            <p className="text-gray-500">{perfil.email}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Badge variant={perfil.tipo === 'INTERNO' ? 'primary' : 'default'}>
                {perfil.tipo}
              </Badge>
              {perfil.roles.map((rol) => (
                <Badge key={rol} variant="info">{rol}</Badge>
              ))}
            </div>
            {perfil.cliente && (
              <p className="text-sm text-gray-500 mt-2">
                Cliente: {perfil.cliente.nombre}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Formulario de edicion */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Informacion Personal</h3>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre"
              {...form.register('nombre')}
              error={form.formState.errors.nombre?.message}
            />
            <Input
              label="Apellido"
              {...form.register('apellido')}
            />
          </div>

          <Input
            label="Email"
            value={perfil.email}
            disabled
            className="bg-gray-50"
          />

          <Input
            label="Teléfono"
            {...form.register('telefono')}
            placeholder="+57 300 123 4567"
          />

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPasswordModalOpen(true)}
            >
              <Key className="h-4 w-4 mr-2" />
              Cambiar Contraseña
            </Button>

            <Button
              type="submit"
              loading={actualizarMutation.isPending}
              disabled={!form.formState.isDirty}
            >
              <Save className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </div>
        </form>
      </Card>

      {/* Información adicional */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Información de la Cuenta</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Cuenta creada:</span>
            <p className="text-gray-900">{formatDateTime(perfil.created_at)}</p>
          </div>
          {perfil.ultimo_acceso && (
            <div>
              <span className="text-gray-500">Último acceso:</span>
              <p className="text-gray-900">{formatDateTime(perfil.ultimo_acceso)}</p>
            </div>
          )}
          <div>
            <span className="text-gray-500">Estado:</span>
            <p className="text-gray-900">
              <Badge variant={perfil.activo ? 'success' : 'danger'}>
                {perfil.activo ? 'Activo' : 'Inactivo'}
              </Badge>
            </p>
          </div>
        </div>
      </Card>

      <CambiarPasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </div>
  );
}
